import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { RiShareLine } from "react-icons/ri";

import { useAppState } from "../../../../context/StateContext";
import { useVideoSurveyInventory } from "../../../../hooks/useVideoSurveyInventory";
import { Customer } from "../../../../models/customer";
import Button, { Color } from "../../../Form/Button";
import Input from "../../../Form/Input";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useMessages from "../../hooks/useMessages/useMessages";
import useShareInventory from "../../hooks/useShareInventory";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import useStyles from "./styles";
import { EmailTemplateToSend } from "Models/templates/email.template";
import { EmailTemplateService } from "Services/templates/email.template.service";

interface Props {
  openModal: (open: boolean) => void;
  customer?: Customer;
  taskId?: number;
  inventoryShared: boolean;
  setInventoryShared: (state: boolean) => void;
}

enum ShareType {
  APP = "app",
  WHATSAPP = "whatsapp",
  EMAIL = "email",
}

const ShareInventory = (props: Props) => {
  const { openModal, customer, taskId, inventoryShared, setInventoryShared } = props;

  const { inventory } = useVideoSurveyInventory();

  const { room } = useVideoContext();

  const localParticipant = room?.localParticipant;

  const { shareInventory } = useMessages(localParticipant);

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const { currentUser } = useAppState();
  const { generatePdf } = useShareInventory();

  const [shareType, setShareType] = useState<string>(ShareType.APP);

  const [emailSended, setEmailSended] = useState<boolean>(false);

  const [email, setEmail] = useState<string>(customer?.email || "");

  const { mutate: sendEmail, isLoading: loadingSendEmail } = useMutation(
    ["send.email"],
    (emailToSend: EmailTemplateToSend) => EmailTemplateService.send(emailToSend),
    {
      onSuccess: () => {
        NotificationToast.success(translate("emailSentSuccesfuly"));
      },
      onError: () => {
        NotificationToast.error(translate("couldNotSendEmail"));
      },
    }
  );

  const generateFile = (itemID: String) => {
    const file = generatePdf();

    const attachInfo = {
      content: file,
      filename: `${itemID}.pdf`,
      type: "application/pdf",
      disposition: "attachment",
      contentId: "mypdf",
    };

    return attachInfo;
  };

  const handleSendEmail = () => {
    if (!currentUser) return;

    const attachInfo = generateFile(String(taskId));

    const emailToSend: EmailTemplateToSend = {
      attachments: [attachInfo],
      from: `${currentUser.name ? currentUser.name : ""} ${
        currentUser.lastName ? currentUser.lastName : ""
      }`,
      to: email,
      html: `
        <div style="padding: 10px;">  <a href="https://shipeezi.com" target="_blank">
          <img src="https://shipeezi-fe-img-assets.s3.ap-southeast-2.amazonaws.com/sd.png" alt="Powered by Shipeezi" style="height: 26px" /></a>
          <hr />
          <br><p>Powered by <a href="https://shipeezi.com" target="_blank">Shipeezi</a></p><hr /><p>Shipeezi, 300 Barangaroo Ave, Sydney NSW 2000, Australia</p>
        </div>
      `,
      subject: `Inventory from ${taskId} survey`,
      bcc: "",
      cc: "",
    };

    sendEmail(emailToSend);
    setEmailSended(true);
  };

  const handleShareInventory = () => {
    const SHARE_TIMEOUT = 10000;

    setInventoryShared(true);
    shareInventory(inventory);

    setTimeout(() => {
      setInventoryShared(false);
    }, SHARE_TIMEOUT);
  };

  const renderEmailComponent = () => (
    <Grid container spacing={2}>
      <Grid item md={12} lg={12}>
        <Input
          name="emailLink"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          placeholder={translate("contactEmailCom")}
        />
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          disabled={email ? email.length === 0 : true}
          onClick={handleSendEmail}
          customColor={Color.SEND}
          className={classes.buttonShare}
          loading={loadingSendEmail}
        >
          {emailSended ? (
            <FaCheck size={14} color="#465869" className="mr-2" />
          ) : (
            <FiSend size={14} color="#465869" className="mr-2" />
          )}
          {emailSended ? translate("sent") : translate("send")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderShareInventoryComponent = () => (
    <Grid container spacing={2}>
      <Grid item container md={12} lg={12} justifyContent="flex-start">
        <Button
          customColor={Color.SEND}
          loading={loadingSendEmail}
          onClick={handleShareInventory}
          disabled={inventoryShared}
        >
          <RiShareLine size={16} className="mr-2" />
          {inventoryShared ? translate("shared") : translate("shareInventory")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderShareComponent = () => {
    switch (shareType) {
      case ShareType.APP:
        return renderShareInventoryComponent();
      case ShareType.EMAIL:
        return renderEmailComponent();
      default:
        return renderShareInventoryComponent();
    }
  };

  const setActiveItem = (value: string): string => {
    if (value === shareType) return "active";

    return "";
  };

  return (
    <section className="w-100">
      <h3 className={classes.title}>{translate("shareTheInventoryWithTheCustomer")}</h3>

      <ul className={classes.shareTypeList}>
        <li className={setActiveItem(ShareType.APP)} onClick={() => setShareType(ShareType.APP)}>
          {translate("shareViaApp")}
        </li>
        <li
          className={setActiveItem(ShareType.EMAIL)}
          onClick={() => setShareType(ShareType.EMAIL)}
        >
          {translate("shareViaEMail")}
        </li>
      </ul>

      {renderShareComponent()}
    </section>
  );
};

export default ShareInventory;
